import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calculator, Info } from 'lucide-react';
import { localeForCountry, fmtMoney } from '../utils/locale';

/**
 * Country-aware Total Cost of Ownership calculator.
 *
 * IE: 1%/2% Revenue.ie stamp duty bands, 1% + 23% VAT legal fees, LPT.
 * UK: SDLT bands (England + N.Ireland; Scotland uses LBTT and Wales LTT
 *     but most of our seeded UK demo set is England/Wales/NI — Scotland
 *     listings should be flagged with a footnote so the user adjusts).
 *
 * Why a single component? Maintenance: anyone tweaking the headline
 * "true cost" copy or default mortgage rate only changes it in one
 * place. The country branch lives inside the cost function.
 */

const calcStampDutyIE = (price) => {
  if (price <= 1_000_000) return price * 0.01;
  return 1_000_000 * 0.01 + (price - 1_000_000) * 0.02;
};

// SDLT (Stamp Duty Land Tax) — England & N.Ireland residential rates
// in force from 1 April 2025 onwards. Source: HMRC SDLT manual.
// Assumes main residence (not additional / non-resident surcharge).
const calcSDLT_UK = (price) => {
  const bands = [
    { upTo: 125_000,  rate: 0.00 },
    { upTo: 250_000,  rate: 0.02 },
    { upTo: 925_000,  rate: 0.05 },
    { upTo: 1_500_000, rate: 0.10 },
    { upTo: Infinity, rate: 0.12 },
  ];
  let prev = 0;
  let tax = 0;
  for (const b of bands) {
    if (price <= prev) break;
    const portion = Math.min(price, b.upTo) - prev;
    tax += Math.max(0, portion) * b.rate;
    prev = b.upTo;
  }
  return tax;
};

const calcLegalFeesIE = (price) => Math.max(1500, price * 0.01 * 1.23);
// UK conveyancing: typically £1,200–£2,500 flat + VAT 20% + searches.
// Use 0.5% capped at £2,500 + 20% VAT, minimum £1,500 incl. VAT.
const calcLegalFeesUK = (price) => Math.max(1500, Math.min(price * 0.005, 2500) * 1.20);

const COST_CONSTANTS = {
  IE: { surveyor: 400,  landRegistry: 800,  councilTaxAnnualPct: 0.001029, vatPct: 0.23 },
  UK: { surveyor: 600,  landRegistry: 300,  councilTaxAnnualPct: 0.005,    vatPct: 0.20 },
  // Council Tax: rough national avg ~£2,170/yr on a £400k home → 0.5% — fine for headline math.
};

const calcMonthlyMortgage = (loanAmount, rateAnnualPct, years) => {
  if (loanAmount <= 0) return 0;
  const r = rateAnnualPct / 100 / 12;
  const n = years * 12;
  if (r === 0) return loanAmount / n;
  return (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

const TotalCostCalculator = ({ askingPrice = 0, country }) => {
  const L = localeForCountry(country);
  const isUK = L.code === 'UK';
  const C = isUK ? COST_CONSTANTS.UK : COST_CONSTANTS.IE;
  const sym = L.currencySymbol;

  // Default rates differ by country (BoE base + spread vs ECB base + spread).
  const [price, setPrice] = useState(askingPrice || 0);
  const [depositPct, setDepositPct] = useState(10);
  const [rate, setRate] = useState(isUK ? 4.5 : 4.0);
  const [termYears, setTermYears] = useState(30);
  const [repairs, setRepairs] = useState(0);

  const fmt = (v) => fmtMoney(v, country);

  const calc = useMemo(() => {
    const stampDuty = isUK ? calcSDLT_UK(price) : calcStampDutyIE(price);
    const legalFees = isUK ? calcLegalFeesUK(price) : calcLegalFeesIE(price);
    const deposit = price * (depositPct / 100);
    const loan = price - deposit;
    const monthlyMortgage = calcMonthlyMortgage(loan, rate, termYears);

    const homeInsurance = (price * 0.0025) / 12;
    const mortgageProtection = (loan * 0.004) / 12;
    const localTaxMonthly = (price * C.councilTaxAnnualPct) / 12;
    const maintenance = (price * 0.01) / 12;

    const monthlyTotal = monthlyMortgage + homeInsurance + mortgageProtection + localTaxMonthly + maintenance;
    const upfrontTotal = deposit + stampDuty + legalFees + C.surveyor + C.landRegistry + Number(repairs || 0);
    const trueCostYear1 = upfrontTotal + monthlyTotal * 12;

    return {
      stampDuty, legalFees, surveyor: C.surveyor, landRegistry: C.landRegistry,
      deposit, loan, monthlyMortgage, homeInsurance, mortgageProtection,
      localTaxMonthly, maintenance, monthlyTotal, upfrontTotal, trueCostYear1,
    };
  }, [price, depositPct, rate, termYears, repairs, isUK, C]);

  const localTaxLabel = isUK ? 'Council Tax (monthly avg)' : 'Local Property Tax (monthly avg)';
  const stampDutyLabel = isUK ? 'Stamp Duty (SDLT)' : 'Stamp duty';
  const landRegistryLabel = isUK ? 'Land Registry & searches (HMLR)' : 'Land Registry & searches';
  const legalLabel = isUK ? `Legal fees (inc. VAT ${Math.round(C.vatPct*100)}%)` : 'Legal fees (inc. VAT)';
  const footnote = isUK
    ? 'Based on current UK SDLT bands (England & N.I.), typical conveyancing/survey fees and estimated monthly ownership. Scotland uses LBTT and Wales uses LTT — adjust accordingly. Figures are estimates — confirm with your solicitor and mortgage provider.'
    : 'Based on current Irish stamp duty bands, typical legal/survey fees and estimated monthly ownership. Figures are estimates — confirm with your solicitor and mortgage provider.';

  return (
    <Card data-testid="total-cost-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-[#7042cc]" />
          Total Cost of Ownership
        </CardTitle>
        <CardDescription>
          The TRUE cost — purchase price + stamp duty + legal fees + repairs + monthly ownership. No hidden surprises.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="tcc-price">Purchase price ({sym})</Label>
            <Input id="tcc-price" data-testid="tcc-input-price" type="number"
              value={price} onChange={(e) => setPrice(Number(e.target.value))} />
          </div>
          <div>
            <Label htmlFor="tcc-deposit">Deposit %</Label>
            <Input id="tcc-deposit" data-testid="tcc-input-deposit" type="number"
              value={depositPct} onChange={(e) => setDepositPct(Number(e.target.value))} />
          </div>
          <div>
            <Label htmlFor="tcc-rate">Mortgage rate %</Label>
            <Input id="tcc-rate" data-testid="tcc-input-rate" type="number" step="0.1"
              value={rate} onChange={(e) => setRate(Number(e.target.value))} />
          </div>
          <div>
            <Label htmlFor="tcc-term">Term (years)</Label>
            <Input id="tcc-term" data-testid="tcc-input-term" type="number"
              value={termYears} onChange={(e) => setTermYears(Number(e.target.value))} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="tcc-repairs">Expected repairs / renovation ({sym})</Label>
            <Input id="tcc-repairs" data-testid="tcc-input-repairs" type="number"
              value={repairs} onChange={(e) => setRepairs(Number(e.target.value))} />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-600">
            Upfront costs
          </div>
          <div className="divide-y divide-gray-100 text-sm">
            <Row label={`Deposit (${depositPct}%)`} value={calc.deposit} fmt={fmt} testid="tcc-row-deposit" />
            <Row label={stampDutyLabel} value={calc.stampDuty} fmt={fmt} testid="tcc-row-stamp" />
            <Row label={legalLabel} value={calc.legalFees} fmt={fmt} testid="tcc-row-legal" />
            <Row label="Surveyor" value={calc.surveyor} fmt={fmt} testid="tcc-row-surveyor" />
            <Row label={landRegistryLabel} value={calc.landRegistry} fmt={fmt} testid="tcc-row-landreg" />
            <Row label="Repairs / renovation" value={Number(repairs || 0)} fmt={fmt} testid="tcc-row-repairs" />
            <Row label="Total upfront" value={calc.upfrontTotal} fmt={fmt} strong testid="tcc-row-upfront-total" />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-600">
            Monthly ownership cost
          </div>
          <div className="divide-y divide-gray-100 text-sm">
            <Row label="Mortgage repayment" value={calc.monthlyMortgage} fmt={fmt} testid="tcc-row-mortgage" />
            <Row label="Home insurance" value={calc.homeInsurance} fmt={fmt} testid="tcc-row-insurance" />
            <Row label="Mortgage protection" value={calc.mortgageProtection} fmt={fmt} testid="tcc-row-mortprot" />
            <Row label={localTaxLabel} value={calc.localTaxMonthly} fmt={fmt} testid="tcc-row-lpt" />
            <Row label="Maintenance reserve (1%/yr)" value={calc.maintenance} fmt={fmt} testid="tcc-row-maintenance" />
            <Row label="Total per month" value={calc.monthlyTotal} fmt={fmt} strong testid="tcc-row-monthly-total" />
          </div>
        </div>

        <div data-testid="tcc-headline"
             className="rounded-lg bg-[#F5EFFF] border border-[#7042cc]/20 p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-[#7042cc] flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-xs font-medium text-[#5b35a8] uppercase tracking-wide mb-1">
              Year 1 true cost
            </div>
            <div className="text-2xl font-semibold text-gray-900">{fmt(calc.trueCostYear1)}</div>
            <div className="text-xs text-gray-600 mt-1">{footnote}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Row = ({ label, value, fmt, strong, testid }) => (
  <div className={`flex items-center justify-between px-4 py-2 ${strong ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700'}`}>
    <span>{label}</span>
    <span data-testid={testid}>{fmt(value)}</span>
  </div>
);

export default TotalCostCalculator;
